import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  signal,
  ViewChild,
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

import { InputText } from 'primeng/inputtext'

import { ForceInvalidClassesDirective, FormControlClassesDirective } from '../../../directives'

@Component({
  selector: 'app-prefixed-input',
  templateUrl: 'prefixed-input.component.html',
  styleUrls: ['prefixed-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InputText, FormControlClassesDirective, ForceInvalidClassesDirective],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: PrefixedInputComponent, multi: true }],
})
export class PrefixedInputComponent implements ControlValueAccessor {
  public readonly prefix = input<string>('')
  public readonly readonly = input<boolean>(false)
  public readonly placeholder = input<string>('')
  public readonly disabled = input<boolean>(false)
  public readonly allowedInput = input<string | RegExp | null | undefined>(undefined)
  public readonly allowUppercase = input<boolean>(false)
  public readonly forceErrorState = input<boolean>()

  protected onChanged?: (value: string) => void
  protected onTouched?: () => void

  protected readonly isDisabled = signal<boolean>(false)
  protected readonly internalValue = signal<string>('')

  protected get pref(): string {
    return (this.prefix() ?? '').trim()
  }

  // Placeholder that automatically prepends the prefix if provided
  protected readonly computedPlaceholder = computed(() => {
    const pref = this.pref
    const placeholder = this.placeholder() ?? ''
    if (pref) {
      return `${pref}- ${placeholder}`
    }
    return placeholder
  })

  // The text displayed in the input element
  protected readonly displayValue = computed(() => {
    const val = this.internalValue()
    if (!val) return ''
    const pref = this.pref
    return pref ? `${pref}-${val}` : val
  })

  @ViewChild('inputRef')
  protected readonly inputRef?: ElementRef<HTMLInputElement>

  // ControlValueAccessor implementation
  writeValue(value: string | null): void {
    const v = value ?? ''
    // Accept both prefixed and raw values
    this.internalValue.set(this.stripPrefix(v))
    // Ensure the rendered input matches new state
    queueMicrotask(() => this.syncDomToDisplayAndCaret())
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChanged = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled)
  }

  protected onInput(raw: string) {
    this.onTouched?.()

    const pref = this.pref

    // If user cleared everything or only prefix remains -> clear completely
    if (
      !raw ||
      (pref && (raw === pref || raw === `${pref} ` || raw === `${pref}-` || raw === `${pref}- `))
    ) {
      this.internalValue.set('')
      this.onChanged?.('')
      this.syncDomToDisplayAndCaret()
      return
    }

    // When there is a prefix, strip it from raw to get the inner value
    let value = raw
    if (pref && raw.startsWith(pref)) {
      value = raw.slice(pref.length)
      if (value.startsWith('-')) {
        value = value.slice(1)
        if (value.startsWith(' ')) value = value.slice(1)
      } else if (value.startsWith(' ')) {
        value = value.slice(1)
      }
    }

    // If user typed without prefix while control had no value, add prefix automatically
    if (pref && !raw.startsWith(pref)) {
      // value is already raw; we want to keep that as inner, and ensure DOM shows pref + value
      // Set inner, emit, then sync DOM (adds prefix) and put caret at end
    }

    // Transform to lowercase during input when uppercase is not allowed
    if (!this.allowUppercase()) {
      value = value.toLowerCase()
    }

    // Apply validation if provided; reject change if it doesn't match
    const rule = this.allowedInput()
    if (rule) {
      const re =
        rule instanceof RegExp
          ? rule
          : (() => {
              try {
                return new RegExp(rule)
              } catch {
                return null
              }
            })()
      if (re && value && !re.test(value)) {
        // Revert DOM to previously valid display without updating the value
        this.syncDomToDisplayAndCaret()
        return
      }
    }

    this.internalValue.set(value)

    const full = value ? this.composeWithPrefix(value) : ''
    this.onChanged?.(full)

    this.syncDomToDisplayAndCaret()
  }

  protected onKeydown(event: KeyboardEvent) {
    this.onTouched?.()
    if (event.key === 'Enter') {
      event.preventDefault()
      this.inputRef?.nativeElement?.blur()
    }
  }

  private syncDomToDisplayAndCaret() {
    const input = this.inputRef?.nativeElement
    if (!input) return
    const display = this.displayValue()
    if (input.value !== display) {
      input.value = display
    }
    // Keep caret at the end after programmatic updates
    const end = input.value.length
    try {
      input.setSelectionRange(end, end)
    } catch {}
  }

  protected stripPrefix(value: string): string {
    const pref = this.pref
    if (!pref) return value
    if (value.startsWith(pref)) {
      let rest = value.slice(pref.length)
      if (rest.startsWith('-')) {
        rest = rest.slice(1)
        if (rest.startsWith(' ')) rest = rest.slice(1)
      } else if (rest.startsWith(' ')) {
        rest = rest.slice(1)
      }
      return rest
    }
    return value
  }

  protected composeWithPrefix(value: string): string {
    const pref = this.pref
    if (!pref) return value
    if (!value) return ''
    return `${pref}-${value}`
  }
}
